import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useBrowseStore } from "@/features/browse";
import { useAuthStore } from "@/features/auth";

export function BrowsePage() {
  const coverage = useBrowseStore((s) => s.coverage);
  const loadCoverage = useBrowseStore((s) => s.loadCoverage);
  const openEmailModal = useAuthStore((s) => s.openEmailModal);
  const user = useAuthStore((s) => s.user);
  const emailUser = useAuthStore((s) => s.emailUser);
  const isSignedUp = !!(user || emailUser);

  useEffect(() => {
    if (!coverage) loadCoverage();
  }, [coverage, loadCoverage]);

  return (
    <div>
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, #818cf8 0%, transparent 50%), radial-gradient(circle at 80% 20%, #6366f1 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 pt-24 pb-20 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Speak any language<br />
            <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">
              through real conversations
            </span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10">
            Immersive dialogs in 100+ languages. Listen to native-speed conversations,
            follow along with romanization and translation, then role-play your part with voice input.
          </p>
          <div className="flex justify-center gap-4">
            {isSignedUp ? (
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-8 py-3 text-lg font-semibold text-white transition-colors hover:bg-indigo-500 shadow-lg shadow-indigo-500/25"
              >
                Browse Dialogs
              </Link>
            ) : (
              <>
                <button
                  onClick={openEmailModal}
                  className="rounded-lg bg-indigo-600 px-8 py-3 text-lg font-semibold text-white transition-colors hover:bg-indigo-500 shadow-lg shadow-indigo-500/25"
                >
                  Get Started Free
                </button>
                <Link
                  to="/"
                  className="rounded-lg bg-slate-800 border border-slate-600 px-8 py-3 text-lg font-semibold text-white transition-colors hover:bg-slate-700"
                >
                  Browse Dialogs
                </Link>
              </>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Access free dialogs. No credit card required.
          </p>
        </div>
      </section>

      {/* Stats bar */}
      <section style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)" }}>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="grid grid-cols-3 gap-8">
            <div>
              <p className="text-4xl font-bold text-white mb-1">
                {coverage ? coverage.total_published.toLocaleString() : "8,500+"}
              </p>
              <p className="text-sm text-slate-400">Published dialogs</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white mb-1">100+</p>
              <p className="text-sm text-slate-400">Languages</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white mb-1">25 yrs</p>
              <p className="text-sm text-slate-400">Of Glossika research</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ background: "#111827" }}>
        <div className="max-w-4xl mx-auto px-4 py-20">
          <h2 className="text-3xl font-bold text-white text-center mb-14">How it works</h2>
          <div className="space-y-12">
            <HowStep
              n={1}
              title="Browse and discover"
              body="Scroll through dialog cards in your target languages. Each dialog is a real conversation with native speakers, organized by CEFR level from A1 to C2."
            />
            <HowStep
              n={2}
              title="Listen, read, and follow along"
              body="Play the full dialog or individual lines. Toggle between native script, romanization,
              and English translation. Adjust playback speed from 0.5x to 2x. Study vocabulary and grammar notes."
            />
            <HowStep
              n={3}
              title="Role-play your part"
              body={`Enter Role-Play mode: pick a speaker, and the conversation comes alive. Hear the
              other speaker's lines, then speak your response. Adjustable helper text from full script
              down to no help at all. Repeat at harder levels until you can hold the conversation from memory.
              Each dialogue becomes your newly acquired language island.`}
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ background: "#0f172a" }}>
        <div className="max-w-5xl mx-auto px-4 py-20">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Built for real language learners
          </h2>
          <p className="text-slate-400 text-center mb-14 max-w-xl mx-auto">
            Every feature comes from 25 years of teaching real language learners.
          </p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-6">
              <h3 className="font-semibold text-white mb-2">Every writing system</h3>
              <p className="text-sm text-slate-400 mb-4">
                Full support for Arabic, Persian, Pashto (Nastaliq), Hebrew, Aramaic (Syriac),
                Armenian, Georgian, Greek, Cyrillic, CJK, Thai, Kannada, and all Latin-script languages.
              </p>
              <div className="bg-slate-900/60 border border-slate-700/30 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">Nastaliq</p>
                <p className="text-2xl leading-relaxed text-slate-200" dir="rtl" style={{ fontFamily: "var(--font-script-nastaliq, 'Noto Nastaliq Urdu', serif)" }}>
                  زه غواړم چې تاسو سره وګورم
                </p>
                <p className="text-xs text-slate-500 italic mt-1">za ghwāṛam che tāso sara wgurum</p>
              </div>
            </div>
            <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-6">
              <h3 className="font-semibold text-white mb-2">Toggle what you see</h3>
              <p className="text-sm text-slate-400 mb-4">
                Show or hide romanization, translation, Nastaliq script, and vocalization (diacritics).
                Customize how much help you get while reading.
              </p>
              <div className="bg-slate-900/60 border border-slate-700/30 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">Vocalized Hebrew</p>
                <p className="text-2xl leading-relaxed text-slate-200" dir="rtl" style={{ fontFamily: "var(--font-script-hebrew, 'Noto Sans Hebrew', sans-serif)" }}>
                  שָׁלוֹם, מַה שְׁלוֹמְךָ?
                </p>
                <p className="text-xs text-slate-500 italic mt-1">shalom, ma shlomkha?</p>
              </div>
            </div>
            <Feature
              title="Always adding more voices"
              body="Most of our languages provide male/female audio with adjustable speed, and we're adding audio support for more languages every month."
            />
            <Feature
              title="RTL and bidirectional"
              body="Arabic, Persian, Pashto, Hebrew, and Aramaic render right-to-left with proper
              text alignment. Mixed-direction content just works."
            />
            <Feature
              title="Role-play with voice"
              body="Speak your lines using your device's microphone. Adjustable helper text level
              from 100% (full script) down to 0% (no help). Practice until it sticks."
            />
            <Feature
              title="One account, all of Glossika"
              body="Sign in once across Glossika's products. Your progress follows you everywhere."
            />
          </div>
        </div>
      </section>

      {/* Founder */}
      <section style={{ background: "#0f172a" }}>
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-8 md:p-10 text-center">
            <p className="text-sm text-indigo-400 font-semibold tracking-widest uppercase mb-4">From the founder</p>
            <p className="text-slate-300 leading-relaxed">
              Developed by our passionate polyglot founder who speaks indigenous languages daily, and has
              developed the syntactic analysis and phonological transcriptions for all of Glossika&apos;s
              products, and now bringing to you a full ecosystem of new products from video learning,
              vocab building, dictionaries, logging progress, alphabets, and drills.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ background: "linear-gradient(180deg, #0c0f1a 0%, #151929 100%)" }}>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          {isSignedUp ? (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Ready to practice?</h2>
              <p className="text-slate-400 mb-8">Browse dialogs in your target languages.</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-8 py-3 text-lg font-semibold text-white transition-colors hover:bg-indigo-500 shadow-lg shadow-indigo-500/25"
              >
                Browse Dialogs
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Start practicing for free</h2>
              <p className="text-slate-400 mb-8">
                Access free dialogs. Sign up in seconds and start your first conversation.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={openEmailModal}
                  className="rounded-lg bg-indigo-600 px-8 py-3 text-lg font-semibold text-white transition-colors hover:bg-indigo-500 shadow-lg shadow-indigo-500/25"
                >
                  Get Started Free
                </button>
                <Link
                  to="/"
                  className="rounded-lg bg-slate-800 border border-slate-600 px-8 py-3 text-lg font-semibold text-white transition-colors hover:bg-slate-700"
                >
                  Browse Dialogs
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function HowStep({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="flex items-start gap-6">
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-bold text-lg">
        {n}
      </div>
      <div>
        <h3 className="font-semibold text-white text-lg mb-2">{title}</h3>
        <p className="text-slate-400 whitespace-pre-line">{body}</p>
      </div>
    </div>
  );
}

function Feature({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-6">
      <h3 className="font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-slate-400">{body}</p>
    </div>
  );
}
